import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  mappingService,
  MappingRegistration,
} from "../services/mappingService";
import { useOperationInProgress } from "../services/unsavedChangesService";
import authService from "../services/authService";
import Header from "./Header";
import BackNav from "./BackNav";
import Loading from "./Loading";
import ConfirmationModal from "./ConfirmationModal";
import JapaneseDatePicker from "./JapaneseDatePicker";
import "./MappingRegistrationList.css";

interface MappingRegistrationListProps {
  user: any;
  onLogout: () => void;
}

interface PaginationInfo {
  total: number;
  perPage: number;
  currentPage: number;
  totalPages: number;
}

const MappingRegistrationList: React.FC<MappingRegistrationListProps> = ({
  user,
  onLogout,
}) => {
  const [mappings, setMappings] = useState<MappingRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    perPage: 25,
    currentPage: 1,
    totalPages: 0,
  });
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentKeyword, setCurrentKeyword] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: number | null;
    name: string;
  }>({
    isOpen: false,
    id: null,
    name: "",
  });
  const [showInvalidSettingsWarning, setShowInvalidSettingsWarning] =
    useState(false);
  const [invalidSettingsChecked, setInvalidSettingsChecked] = useState(false);

  // Separate loading states for better UX
  const [invalidSettingsLoading, setInvalidSettingsLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper functions to convert between Date objects and string formats
  const dateToString = (date: Date | null): string => {
    if (!date) return "";
    // Use local timezone to avoid date shifting
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // YYYY-MM-DD format
  };

  const stringToDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    // Parse date in local timezone to avoid timezone issues
    const [year, month, day] = dateString.split("-").map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return isNaN(date.getTime()) ? null : date;
  };

  // Check app_cd before loading data
  const checkAppCdAndLoad = async () => {
    try {
      setInvalidSettingsLoading(true);
      const response = await authService.getMe();

      if (
        response.success &&
        response.data?.appCd &&
        response.data?.isHasCollaboAPIKey === true &&
        response.data?.isHasCollaboUserid === true
      ) {
        // App_cd exists, proceed with data loading
        setInvalidSettingsChecked(true);
        setInvalidSettingsLoading(false);
        setDataLoading(true);

        const page = parseInt(searchParams.get("page") || "1");
        const keyword = searchParams.get("keyword") || "";
        const startDateParam = searchParams.get("startDate") || "";
        const endDateParam = searchParams.get("endDate") || "";

        // Convert string dates to Date objects
        const startDateObj = stringToDate(startDateParam);
        const endDateObj = stringToDate(endDateParam);

        setPagination(prev => ({
          ...prev,
          currentPage: page,
        }));
        setSearchTerm(keyword);
        setCurrentKeyword(keyword);
        setStartDate(startDateObj);
        setEndDate(endDateObj);

        await loadMappings(page, keyword, startDateParam, endDateParam);
        setDataLoading(false);
      } else {
        // App_cd doesn't exist, show warning and stop loading
        setInvalidSettingsChecked(true);
        setInvalidSettingsLoading(false);
        setDataLoading(false);
        setShowInvalidSettingsWarning(true);
      }
    } catch (error: any) {
      console.error("Error checking app_cd:", error);

      // Handle timeout errors specifically
      if (error.isTimeout) {
        // For timeout, don't show warning popup, just log the error
        setInvalidSettingsChecked(true);
        setInvalidSettingsLoading(false);
        setDataLoading(false);
        return;
      }

      // On other errors, show warning and stop loading
      setInvalidSettingsChecked(true);
      setInvalidSettingsLoading(false);
      setDataLoading(false);
      setShowInvalidSettingsWarning(true);
    }
  };

  // Initialize state from URL parameters on component mount
  // Supports URLs like: /mapping_registration_list?page=2&keyword=search_term&startDate=2023-08-01&endDate=2023-08-05
  useEffect(() => {
    checkAppCdAndLoad();
  }, []);

  // Update URL parameters when pagination, search, or date filters change
  // Only adds parameters if they have meaningful values
  const updateURL = (
    page: number,
    keyword?: string,
    startDate?: Date | null,
    endDate?: Date | null
  ) => {
    const params = new URLSearchParams();
    if (page > 1) {
      params.set("page", page.toString());
    }
    if (keyword && keyword.trim()) {
      params.set("keyword", keyword.trim());
    }
    if (startDate) {
      params.set("startDate", dateToString(startDate));
    }
    if (endDate) {
      params.set("endDate", dateToString(endDate));
    }
    setSearchParams(params);
  };

  // Use operation in progress service
  const { handleLogoClick, handleLogoutClick } = useOperationInProgress({
    operationInProgress,
    warningMessage: "操作が進行中です。このページを離れますか？",
  });

  const loadMappings = async (
    page: number = 1,
    keyword?: string,
    startDate?: string,
    endDate?: string
  ) => {
    try {
      setLoading(true);
      setOperationInProgress(true);
      setError(null);
      const response = await mappingService.getMappingRegistrations(
        page,
        pagination.perPage,
        keyword,
        startDate,
        endDate
      );
      if (response.success) {
        setMappings(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.total || response.data.length,
          currentPage: page,
          totalPages: Math.ceil(
            (response.total || response.data.length) / pagination.perPage
          ),
        }));
        setCurrentKeyword(keyword || "");
      } else {
        setError(response.message || "マッピング一覧の取得に失敗しました");
      }
      setLoading(false);
    } catch (err: any) {
      setError(err.message || "マッピング一覧の取得に失敗しました");
      setLoading(false);
    } finally {
      setLoading(false);
      setOperationInProgress(false);
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    if (endDate && date && date > endDate) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    if (startDate && date && date < startDate) {
      setStartDate(null);
    }
  };

  const handleCreateNew = () => {
    navigate("/mapping_registration_list/new");
  };

  const handleAppCdWarningConfirm = () => {
    setShowInvalidSettingsWarning(false);
    navigate("/settings");
  };

  const handleAppCdWarningCancel = () => {
    setShowInvalidSettingsWarning(false);
    navigate("/");
  };

  const handleEdit = (id: number) => {
    navigate(`/mapping_registration_list/edit/${id}`);
  };

  const handleDelete = (id: number) => {
    const mapping = mappings.find(m => m.id === id);
    setDeleteModal({
      isOpen: true,
      id: id,
      name: mapping?.collaboFormName || "",
    });
  };

  const confirmDelete = async () => {
    if (deleteModal.id) {
      try {
        setOperationInProgress(true);
        const response = await mappingService.deleteMappingRegistration(
          deleteModal.id
        );
        if (response.success) {
          await loadMappings(pagination.currentPage, currentKeyword); // Reload the current page
        }
      } catch (err: any) {
        console.error("Delete error:", err);
      } finally {
        setOperationInProgress(false);
        setDeleteModal({ isOpen: false, id: null, name: "" });
      }
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, id: null, name: "" });
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      updateURL(page, currentKeyword, startDate, endDate);
      loadMappings(
        page,
        currentKeyword,
        dateToString(startDate),
        dateToString(endDate)
      );
    }
  };

  const handleSearch = () => {
    // Always allow search, even if no changes detected
    const keyword = searchTerm.trim();
    updateURL(1, keyword, startDate, endDate);
    loadMappings(1, keyword, dateToString(startDate), dateToString(endDate));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setStartDate(null);
    setEndDate(null);
    updateURL(1, "", null, null);
    loadMappings(1, "", "", "");
  };

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, pagination.currentPage - 3);
    const endPage = Math.min(pagination.totalPages, pagination.currentPage + 3);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "Asia/Tokyo",
    });
  };

  // Show loading for app_cd check
  if (invalidSettingsLoading) {
    return (
      <div className="mapping-list-container">
        <Loading message="アプリケーション設定を確認中..." fullHeight={true} />
      </div>
    );
  }

  // Show loading for data loading
  if (dataLoading) {
    return (
      <div className="mapping-list-container">
        <Loading message="マッピング一覧を読み込み中..." fullHeight={true} />
      </div>
    );
  }

  // Show loading for other operations
  if (loading || !invalidSettingsChecked) {
    return (
      <div className="mapping-list-container">
        <Loading message="マッピング一覧を読み込み中..." fullHeight={true} />
      </div>
    );
  }

  return (
    <div className="mapping-list-container">
      {/* Header */}
      <Header
        user={user}
        onLogout={onLogout}
        onLogoClick={handleLogoClick}
        onLogoutClick={() => handleLogoutClick(onLogout)}
      />
      {/* Back Button (aligned with content) */}
      <BackNav label="ダッシュボードに戻る" to="/" />

      {/* Content */}
      <div className="mapping-list-content">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">マッピング一覧</h1>
          <p className="page-description">
            登録済みのマッピング設定の一覧表示・管理を行います
          </p>
        </div>

        {/* Separator */}
        <div className="separator"></div>

        {/* Controls Section */}
        <div className="controls-section">
          <div className="filters-left">
            <div className="filter-group">
              <label className="filter-label">作成日</label>
              <div className="date-filters">
                <JapaneseDatePicker
                  selected={startDate}
                  onChange={handleStartDateChange}
                  placeholder="yyyy/mm/dd"
                  maxDate={endDate}
                  className="date-input"
                />
                <span className="date-separator">〜</span>
                <JapaneseDatePicker
                  selected={endDate}
                  onChange={handleEndDateChange}
                  placeholder="yyyy/mm/dd"
                  minDate={startDate}
                  className="date-input"
                />
              </div>
            </div>
            <div className="search-container">
              <div className="search-wrapper">
                <input
                  type="text"
                  className="search-input"
                  placeholder="検索キーワードを入力"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  maxLength={255}
                />
                <button
                  className="search-button"
                  onClick={handleSearch}
                  disabled={searchTerm.length > 255}
                  title="検索"
                >
                  検索
                </button>
                {(currentKeyword || startDate || endDate) && (
                  <button
                    className="clear-search-button"
                    onClick={handleClearSearch}
                    title="フィルターをクリア"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
          <button className="create-button" onClick={handleCreateNew}>
            新規マッピング作成
          </button>
        </div>

        {/* Table Section */}
        <div className="table-section">
          {error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button
                className="retry-button"
                onClick={() => loadMappings(pagination.currentPage)}
              >
                再試行
              </button>
            </div>
          ) : mappings.length === 0 ? (
            <div className="empty-container">
              <p className="empty-message">データがありません</p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="mapping-table">
                  <thead>
                    <tr>
                      <th className="id-column">No</th>
                      <th className="name-column">申請書名</th>
                      <th className="created-column">作成日</th>
                      <th className="updated-column">最終更新日</th>
                      <th className="actions-column">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map((mapping, index) => (
                      <tr
                        key={mapping.id}
                        className={`mapping-row ${index % 2 === 0 ? "even" : "odd"}`}
                      >
                        <td className="id-cell">
                          {(pagination.currentPage - 1) * pagination.perPage +
                            index +
                            1}
                        </td>
                        <td className="name-cell">{mapping.collaboFormName}</td>
                        <td className="created-cell">
                          {formatDate(mapping.createdAt)}
                        </td>
                        <td className="updated-cell">
                          {formatDate(mapping.updatedAt)}
                        </td>
                        <td className="actions-cell">
                          <button
                            className="edit-button"
                            onClick={() => handleEdit(mapping.id)}
                            title="編集"
                          >
                            編集
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDelete(mapping.id)}
                            title="削除"
                          >
                            削除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination - Only show when there is data */}
              {mappings.length > 0 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    {(pagination.currentPage - 1) * pagination.perPage + 1} -{" "}
                    {Math.min(
                      pagination.currentPage * pagination.perPage,
                      pagination.total
                    )}{" "}
                    / {pagination.total}
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-button"
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.currentPage === 1}
                      title="最初のページ"
                    >
                      «
                    </button>
                    <button
                      className="pagination-button"
                      onClick={() =>
                        handlePageChange(pagination.currentPage - 1)
                      }
                      disabled={pagination.currentPage === 1}
                      title="前のページ"
                    >
                      ‹
                    </button>
                    {getPageNumbers().map(page => (
                      <button
                        key={page}
                        className={`pagination-button ${page === pagination.currentPage ? "active" : ""}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="pagination-button"
                      onClick={() =>
                        handlePageChange(pagination.currentPage + 1)
                      }
                      disabled={
                        pagination.currentPage === pagination.totalPages
                      }
                      title="次のページ"
                    >
                      ›
                    </button>
                    <button
                      className="pagination-button"
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={
                        pagination.currentPage === pagination.totalPages
                      }
                      title="最後のページ"
                    >
                      »
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="削除の確認"
        message={`このマッピング情報を削除してもよろしいですか？\n この操作は元に戻せません。`}
        confirmText="削除"
        cancelText="キャンセル"
        confirmButtonColor="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* App CD Warning Modal */}
      <ConfirmationModal
        isOpen={showInvalidSettingsWarning}
        title="設定が必要です"
        message="設定されたコラボフローの認証情報が正しくないか、無効になっている可能性があります。管理者設定でご確認ください。"
        confirmText="管理者設定へ"
        cancelText="キャンセル"
        confirmButtonColor="primary"
        onConfirm={handleAppCdWarningConfirm}
        onCancel={handleAppCdWarningCancel}
      />
    </div>
  );
};

export default MappingRegistrationList;
